// Open a new window containing only the print-target HTML and trigger printing there.
// This avoids dialog/portal/visibility issues and gives a clean thermal-receipt sized output.
const STYLE = `
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: white; }
  body {
    font-family: 'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace;
    color: #000;
    width: 80mm;
    padding: 3mm 4mm;
    font-size: 10pt;
    line-height: 1.35;
  }
  .receipt h1, .receipt h2, .receipt h3, .receipt .font-display {
    font-family: 'Fraunces', Georgia, serif;
    font-weight: 800;
    letter-spacing: -0.01em;
    margin: 0;
  }
  .receipt .center { text-align: center; }
  .receipt .row { display: flex; justify-content: space-between; gap: 8px; }
  .receipt .muted { color: #555; }
  .receipt .small { font-size: 8pt; }
  .receipt .xsmall { font-size: 7.5pt; }
  .receipt .bold { font-weight: 700; }
  .receipt .divider { border-top: 1px dashed #777; margin: 2.5mm 0; }
  .receipt .item { margin-bottom: 2mm; }
  .receipt .item .mods { padding-left: 3mm; color: #444; font-size: 8.5pt; }
  .receipt .total-row { font-size: 12pt; font-weight: 700; }
  .receipt .title { font-size: 11pt; font-weight: 800; letter-spacing: 0.02em; }
  .receipt .kot-title { font-size: 13pt; font-weight: 900; }
  .receipt .kot-pill {
    display: inline-block; padding: 1mm 2mm; border: 1px solid #000; border-radius: 2mm; font-size: 8pt; letter-spacing: 0.1em;
  }
`;

export function printNode(htmlString, title = "Receipt") {
  const win = window.open("", "_blank", "width=400,height=600");
  if (!win) return;
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${STYLE}</style></head><body><div class="receipt">${htmlString}</div></body></html>`);
  win.document.close();
  win.focus();
  // Give the browser a tick to render fonts then print
  setTimeout(() => {
    win.print();
    // Close after print dialog dismissed (best effort)
    setTimeout(() => win.close(), 300);
  }, 250);
}

const money = (n, c) => `${c || ""}${Number(n || 0).toFixed(2)}`;

export function buildReceiptHTML(order, settings = {}) {
  const c = settings.currency || "₹";
  const date = new Date(order.created_at);
  const itemsHTML = order.items.map((it) => `
    <div class="item">
      <div class="row"><span class="bold">${it.quantity}× ${escape(it.name)}</span><span>${money(it.line_total, c)}</span></div>
      <div class="mods">${escape(it.size || "")}${it.sugar ? ` · Sugar ${escape(it.sugar)}` : ""}${it.ice ? ` · Ice ${escape(it.ice)}` : ""}</div>
      ${it.toppings && it.toppings.length ? `<div class="mods">+ ${it.toppings.map((t) => escape(t.name)).join(", ")}</div>` : ""}
      ${it.notes ? `<div class="mods"><i>Note: ${escape(it.notes)}</i></div>` : ""}
    </div>
  `).join("");
  return `
    <div class="center">
      <div class="title">${escape(settings.outlet_name || "Hizli")}</div>
      <div class="xsmall muted">BOBA TEA</div>
    </div>
    <div class="divider"></div>
    <div class="row small">
      <span>Order #${order.order_number}</span>
      <span>${date.toLocaleString()}</span>
    </div>
    <div class="small muted">Served by ${escape(order.staff_name || "—")}</div>
    <div class="divider"></div>
    ${itemsHTML}
    <div class="divider"></div>
    <div class="row"><span>Subtotal</span><span>${money(order.subtotal, c)}</span></div>
    ${order.discount > 0 ? `<div class="row"><span>Discount</span><span>- ${money(order.discount, c)}</span></div>` : ""}
    ${order.packaging_total > 0 ? `<div class="row"><span>Packaging</span><span>${money(order.packaging_total, c)}</span></div>` : ""}
    <div class="row"><span>Tax</span><span>${money(order.tax, c)}</span></div>
    <div class="divider"></div>
    <div class="row total-row"><span>TOTAL</span><span>${money(order.total, c)}</span></div>
    <div class="divider"></div>
    <div class="row small"><span>Paid · ${escape((order.payment_method || "").toUpperCase())}</span><span>${money(order.payment_method === "cash" ? order.cash_received : order.total, c)}</span></div>
    ${order.change != null ? `<div class="row small"><span>Change</span><span>${money(order.change, c)}</span></div>` : ""}
    <div class="divider"></div>
    <div class="center small muted">Thank you! See you again 🧋</div>
  `;
}

export function buildKOTHTML({ order, kot_batch, kot_items }, settings = {}) {
  const date = new Date(kot_batch.printed_at);
  const itemsHTML = kot_items.map((it) => `
    <div class="item">
      <div class="row bold" style="font-size: 12pt;"><span>${it.quantity}× ${escape(it.name)}</span></div>
      <div class="mods">${escape(it.size || "")}${it.sugar ? ` · Sugar ${escape(it.sugar)}` : ""}${it.ice ? ` · Ice ${escape(it.ice)}` : ""}</div>
      ${it.toppings && it.toppings.length ? `<div class="mods">+ ${it.toppings.map((t) => escape(t.name)).join(", ")}</div>` : ""}
      ${it.notes ? `<div class="mods bold"><i>✱ ${escape(it.notes)}</i></div>` : ""}
    </div>
  `).join("");
  return `
    <div class="center">
      <div class="kot-pill">KITCHEN ORDER</div>
      <div class="kot-title" style="margin-top: 2mm;">${escape(settings.outlet_name || "Hizli")}</div>
    </div>
    <div class="divider"></div>
    <div class="row bold"><span>KOT #${kot_batch.kot_number}</span><span class="small">${date.toLocaleTimeString()}</span></div>
    <div class="row small"><span>Order #${order.order_number}</span><span>Seq ${kot_batch.seq}</span></div>
    <div class="small muted">Staff: ${escape(order.staff_name || "—")}</div>
    <div class="divider"></div>
    ${itemsHTML}
    <div class="divider"></div>
    <div class="center xsmall muted">— PREPARE THESE ITEMS —</div>
  `;
}

function escape(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
